import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SocialFeedService } from './services/social-feed.service';
import { SocialHubService } from './services/social-hub.service';
import { SocialFeedScope, SocialPost } from './models/social-feed.model';

type FeedTab = 'following' | 'discover' | 'communities' | 'maps' | 'games' | 'mine';

const SCOPES: Record<FeedTab, SocialFeedScope | undefined> = {
  following: undefined, // people you follow (no scope)
  discover: 'discover', // other players' public posts
  communities: 'communities',
  maps: 'map_groups',
  games: 'game_groups', // GameMaster games, a different thing from maps
  mine: 'my_posts',
};

/**
 * The social feed: a composer, then posts by tab. The frame (your profile,
 * shortcuts, notifications, communities) comes from app-social-shell, and
 * each post is an app-social-post-card. The tab is kept in the address.
 */
@Component({
  selector: 'app-social-feed',
  templateUrl: './social-feed.component.html',
  styleUrls: ['./social-feed.component.scss'],
  standalone: false,
})
export class SocialFeedComponent implements OnInit {
  readonly tabs: { id: FeedTab; label: string }[] = [
    { id: 'following', label: 'Following' },
    { id: 'discover', label: 'Discover' },
    { id: 'communities', label: 'Communities' },
    { id: 'maps', label: 'Maps' },
    { id: 'games', label: 'Games' },
    { id: 'mine', label: 'My posts' },
  ];
  tab: FeedTab = 'following';

  posts: SocialPost[] = [];
  loading = false;
  feedError = false;
  page = 1;
  hasMore = false;
  loadingMore = false;
  showBackToTop = false;
  readonly skeletons = [1, 2, 3];

  /** A tab switch while a request is in flight makes its answer stale. */
  private seq = 0;

  constructor(
    private social: SocialFeedService,
    public hub: SocialHubService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const wanted = this.route.snapshot.queryParamMap.get('tab') as FeedTab | null;
    if (wanted && wanted in SCOPES) this.tab = wanted;
    this.load();
  }

  selectTab(tab: FeedTab): void {
    if (this.tab === tab) return;
    this.tab = tab;
    this.router.navigate([], { queryParams: { tab: tab === 'following' ? null : tab }, replaceUrl: true });
    this.posts = [];
    this.load();
  }

  load(): void {
    this.loading = true;
    this.feedError = false;
    const seq = ++this.seq;
    this.social.getFeed({ scope: SCOPES[this.tab] }).subscribe({
      next: (page) => {
        if (seq !== this.seq) return;
        this.posts = page.results;
        this.page = 1;
        this.hasMore = !!page.next;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (seq !== this.seq) return;
        this.posts = [];
        this.hasMore = false;
        this.loading = false;
        this.feedError = true;
        this.cdr.markForCheck();
      },
    });
  }

  loadMore(): void {
    if (this.loadingMore || !this.hasMore) return;
    this.loadingMore = true;
    const seq = this.seq;
    const next = this.page + 1;
    this.social.getFeed({ scope: SCOPES[this.tab], page: next }).subscribe({
      next: (page) => {
        this.loadingMore = false;
        if (seq !== this.seq) return;
        // Offset paging can repeat a post if new ones arrived meanwhile.
        const have = new Set(this.posts.map((p) => p.id));
        this.posts = [...this.posts, ...page.results.filter((p) => !have.has(p.id))];
        this.page = next;
        this.hasMore = !!page.next;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingMore = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** A new post may not belong to this tab, so ask the server again. */
  onPosted(): void {
    this.load();
  }

  onRemoved(id: number): void {
    this.posts = this.posts.filter((p) => p.id !== id);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    const show = window.scrollY > window.innerHeight * 0.8;
    if (show !== this.showBackToTop) this.showBackToTop = show;
  }

  scrollToTop(): void {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  }
}
